/**
 * Loads the user manual out of `docs/manual/` at build time.
 *
 * The markdown files in the repo ARE the in-app help — there is no second copy
 * to keep in sync. Adding a `.md` file under docs/manual publishes a new help
 * page automatically; nothing here needs editing.
 *
 * Conventions the loader relies on:
 *   - the first `# ` line is the page title
 *   - a leading numeric prefix on the filename controls order (01-, 02-, ...)
 *   - the containing directory is the sidebar section
 */

const rawDocs = import.meta.glob('../../../../docs/manual/**/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

/**
 * Screenshots live beside the markdown so a relative `![](images/x.png)` works
 * on disk and on GitHub. Vite hashes them into the build, so the app has to look
 * the served URL up by path rather than using the relative src directly.
 */
const rawImages = import.meta.glob('../../../../docs/manual/**/*.{png,jpg,jpeg,gif,svg,webp}', {
  import: 'default',
  eager: true,
}) as Record<string, string>;

/** Sidebar section order. Directories not listed here sort to the end. */
const SECTION_ORDER = ['', 'workflows', 'reference'];

const SECTION_LABELS: Record<string, string> = {
  '': 'Getting Started',
  workflows: 'Workflows',
  reference: 'Reference',
};

export interface ManualHeading {
  id: string;
  text: string;
  level: 2 | 3;
}

export interface ManualDoc {
  /** Route slug, e.g. "core-concepts" or "workflows/bill-a-finished-job". */
  slug: string;
  title: string;
  section: string;
  sectionLabel: string;
  body: string;
  headings: ManualHeading[];
}

export interface ManualSection {
  key: string;
  label: string;
  docs: ManualDoc[];
}

/**
 * Turns heading text into a URL fragment. The renderer uses this same function
 * for its `id` attributes, so sidebar links and anchors can never drift apart.
 *
 * Manual headings are numbered ("1.2 The three status tracks"), which would
 * produce an id starting with a digit — legal HTML, but not a valid CSS
 * selector. The `s-` prefix keeps ids usable from querySelector too.
 */
export function slugifyHeading(text: string): string {
  const slug = text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');

  return /^\d/.test(slug) ? `s-${slug}` : slug;
}

function titleFromBody(body: string, fallback: string): string {
  const match = body.match(/^#\s+(.+)$/m);
  if (!match?.[1]) return fallback;
  // "Part 1 — How jobOS Thinks" reads better in a sidebar as just the subject,
  // and the section header already says Workflow / Reference.
  return match[1].replace(/^(Part\s+\d+|Workflow|Reference)\s*[—–-]\s*/i, '').trim();
}

function prettifyFilename(name: string): string {
  return name
    .replace(/^\d+[-_]/, '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function extractHeadings(body: string): ManualHeading[] {
  const headings: ManualHeading[] = [];
  let inFence = false;

  for (const line of body.split('\n')) {
    if (line.startsWith('```')) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;

    const match = line.match(/^(#{2,3})\s+(.+?)\s*$/);
    if (!match?.[1] || !match[2]) continue;

    // Strip inline markdown so the TOC shows plain text.
    const text = match[2].replace(/[*_`]/g, '');
    headings.push({
      id: slugifyHeading(text),
      text,
      level: match[1].length as 2 | 3,
    });
  }

  return headings;
}

function buildDocs(): ManualDoc[] {
  return Object.entries(rawDocs)
    .map(([path, body]) => {
      // ".../docs/manual/workflows/bill-a-finished-job.md" -> "workflows/bill-a-finished-job.md"
      const relative = path.slice(path.indexOf('/docs/manual/') + '/docs/manual/'.length);
      const segments = relative.replace(/\.md$/, '').split('/');
      const filename = segments.pop() as string;
      const section = segments.join('/');
      const sortKey = filename;

      // Numeric prefixes order the files on disk but don't belong in the URL.
      const slug = [...segments, filename.replace(/^\d+[-_]/, '')].join('/');

      return {
        slug,
        title: titleFromBody(body, prettifyFilename(filename)),
        section,
        sectionLabel: SECTION_LABELS[section] ?? prettifyFilename(section),
        body,
        headings: extractHeadings(body),
        sortKey,
      };
    })
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey, undefined, { numeric: true }))
    .map(({ sortKey: _sortKey, ...doc }) => doc);
}

export const manualDocs: ManualDoc[] = buildDocs();

export const manualSections: ManualSection[] = (() => {
  const bySection = new Map<string, ManualDoc[]>();
  for (const doc of manualDocs) {
    const existing = bySection.get(doc.section);
    if (existing) existing.push(doc);
    else bySection.set(doc.section, [doc]);
  }

  return [...bySection.entries()]
    .sort(([a], [b]) => {
      const ai = SECTION_ORDER.indexOf(a);
      const bi = SECTION_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    })
    .map(([key, docs]) => ({
      key,
      label: docs[0]?.sectionLabel ?? key,
      docs,
    }));
})();

/**
 * Manual-relative markdown paths as they exist on disk, prefixes included
 * (e.g. "reference/05-troubleshooting.md"). Slugs drop those prefixes, so a
 * link can resolve in the app while being a dead file link on GitHub — this is
 * what lets a test catch that.
 */
export const manualFilePaths: string[] = Object.keys(rawDocs).map((path) =>
  path.slice(path.indexOf('/docs/manual/') + '/docs/manual/'.length)
);

/** Manual-relative image path (e.g. "images/jobs-list.png") → served URL. */
export const manualImages: Record<string, string> = Object.fromEntries(
  Object.entries(rawImages).map(([path, url]) => [
    path.slice(path.indexOf('/docs/manual/') + '/docs/manual/'.length),
    url,
  ])
);

export function getDoc(slug: string | undefined): ManualDoc | undefined {
  if (!slug) return manualDocs[0];
  return manualDocs.find((doc) => doc.slug === slug);
}

export interface SearchHit {
  doc: ManualDoc;
  /** Surrounding text for the first match, with no markdown syntax. */
  snippet: string;
}

/**
 * Plain substring search over titles and body text. The manual is a handful of
 * documents, so this runs comfortably on every keystroke without an index.
 */
export function searchManual(query: string): SearchHit[] {
  const needle = query.trim().toLowerCase();
  if (needle.length < 2) return [];

  const hits: SearchHit[] = [];

  for (const doc of manualDocs) {
    const titleMatch = doc.title.toLowerCase().includes(needle);

    // Flatten the markdown so snippets don't come out full of pipes and hashes.
    const plain = doc.body
      .replace(/```[\s\S]*?```/g, ' ')
      .replace(/[#>|*_`]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const index = plain.toLowerCase().indexOf(needle);
    if (index === -1 && !titleMatch) continue;

    const start = Math.max(0, index - 60);
    const snippet =
      index === -1
        ? plain.slice(0, 140)
        : `${start > 0 ? '…' : ''}${plain.slice(start, index + needle.length + 80)}…`;

    hits.push({ doc, snippet });
  }

  return hits;
}
